import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { copyToClipboard } from "@/lib/utils";
import { Copy, RefreshCw } from "lucide-react";
import { ToolPageLayout } from "@/components/ToolPageLayout";

export function PasswordGenerator() {
  const [password, setPassword] = useState("");
  const [length, setLength] = useState(16);
  const [options, setOptions] = useState({
    uppercase: true,
    lowercase: true,
    numbers: true,
    symbols: true,
  });
  const [copied, setCopied] = useState(false);

  const generatePassword = useCallback(() => {
    let charset = "";
    if (options.lowercase) charset += "abcdefghijklmnopqrstuvwxyz";
    if (options.uppercase) charset += "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    if (options.numbers) charset += "0123456789";
    if (options.symbols) charset += "!@#$%^&*()_+~`|}{[]:;?><,./-=";

    if (!charset) {
      setPassword("");
      return;
    }

    let newPassword = "";
    const charsetLength = charset.length;
    const array = new Uint32Array(length);
    window.crypto.getRandomValues(array);
    
    for (let i = 0; i < length; i++) {
      newPassword += charset[array[i] % charsetLength];
    }
    
    setPassword(newPassword);
  }, [length, options]);

  useEffect(() => {
    generatePassword();
  }, [generatePassword]);

  const handleCopy = () => {
    if (!password) return;
    copyToClipboard(password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const getStrengthInfo = () => {
    if (length < 8) return { label: "Weak", color: "bg-red-500", text: "text-red-600" };
    
    let entropy = 0;
    if (options.lowercase) entropy += 26;
    if (options.uppercase) entropy += 26;
    if (options.numbers) entropy += 10;
    if (options.symbols) entropy += 32;
    
    const totalEntropy = length * Math.log2(entropy || 1);
    
    if (totalEntropy < 40) return { label: "Weak", color: "bg-red-500", text: "text-red-600" };
    if (totalEntropy < 60) return { label: "Moderate", color: "bg-yellow-500", text: "text-yellow-600" };
    if (totalEntropy < 80) return { label: "Strong", color: "bg-green-500", text: "text-green-600" };
    return { label: "Very Strong", color: "bg-blue-600", text: "text-blue-700" };
  };

  const strength = getStrengthInfo();

  return (
    <ToolPageLayout
      title="Secure Password Generator Online"
      description="Generate strong, secure, and random passwords instantly with our free online password generator tool. Customize length and character types."
      h1="Password Generator"
      category="JWT & Security"
      categoryPath="/jwt-security"
      howToUse={[
        "Set the desired password length.",
        "Toggle character options (uppercase, lowercase, numbers, symbols).",
        "Click 'Generate New Password' to create a new secure password.",
        "Click the copy button to save it."
      ]}
      faq={[
        { question: "What is a secure password generator and why should I use one?", answer: "A secure password generator is a local application or online utility that constructs highly complex, random, and unpredictable character strings. Using one prevents credential stuffing, brute-force attacks, and social engineering hacks by avoiding recognizable patterns or common dictionary words." },
        { question: "What makes a password cryptographically strong and safe?", answer: "A strong password relies on maximum entropy—achieved by using a long string length (ideally 16+ characters) combined with a high-entropy mix of uppercase letters, lowercase letters, numerical digits, and special symbols (like @, #, $, %). This maximizes the possible character combinations." },
        { question: "What is password entropy and how is it calculated?", answer: "Password entropy measures a password's unpredictable complexity in bits. The formula is E = L * log2(R), where L is the password length and R is the size of the character pool (e.g., 94 for full ASCII). An entropy rating above 80 bits is considered exceptionally strong and virtually uncrackable." },
        { question: "How long should my passwords be to remain secure?", answer: "In modern cybersecurity, a length of at least 12 to 16 characters is the baseline recommendation. For highly critical accounts like your primary email, banking services, or password vaults, setting a length of 20 to 32 characters provides maximum defense-in-depth security." },
        { question: "Is this online password generator safe from data leaks?", answer: "Yes, this password generator runs 100% locally inside your web browser. It leverages your browser's client-side window.crypto engine to generate characters offline. No passwords or user parameters are ever transmitted over the internet or saved to any server." },
        { question: "What characters should I include or exclude in a complex password?", answer: "Including a mix of lowercase (a-z), uppercase (A-Z), digits (0-9), and special characters (!@#$%^&*) is standard. To avoid user confusion, some systems exclude ambiguous, hard-to-read characters like 'l' (lowercase L), 'I' (uppercase i), '1' (one), 'o' (lowercase O), and '0' (zero)." },
        { question: "How do modern password managers generate random credentials?", answer: "Password managers use cryptographically secure pseudo-random number generators (CSPRNG) tied to system-level noise. This ensures that every generated password has uniform distribution, making it mathematically impossible to predict or pre-calculate." },
        { question: "What is a passphrase and is it more secure than a random password?", answer: "A passphrase is a sequence of random dictionary words (e.g., 'correct-battery-horse-staple'). Passphrases are often easier for humans to memorize while maintaining high entropy due to their extreme length, whereas random passwords of shorter length are ideal for automated copy-paste workflows." },
        { question: "Why is reusing the same password across multiple websites extremely dangerous?", answer: "If an attacker compromises a single insecure forum or website where you reused a password, they will use automated bots to test that exact email and password combination on thousands of other major sites (an exploit known as credential stuffing)." },
        { question: "How does a brute-force or dictionary attack compromise weak passwords?", answer: "Brute-force attacks use automated software to rapidly try every possible key combination. Dictionary attacks target common words, names, dates, and predictable variations (like replacing 's' with '$'). A truly random, high-entropy password render these automated cracking methods useless." }
      ]}
    >
      <div className="max-w-2xl mx-auto space-y-8">
        <div className="space-y-4">
          <div className="relative">
            <Input 
              value={password}
              readOnly
              className="text-2xl font-mono h-16 pr-24 text-slate-900 tracking-wider"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
              <Button variant="ghost" size="icon" onClick={generatePassword} title="Regenerate" aria-label="Regenerate password">
                <RefreshCw className="h-5 w-5 text-slate-500" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleCopy} title="Copy" aria-label="Copy password to clipboard">
                <Copy className="h-5 w-5 text-slate-500" />
              </Button>
            </div>
          </div>
          
          {copied && <div className="text-sm text-green-600 text-center">Password copied to clipboard!</div>}
          
          <div className="flex items-center gap-4">
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-300 ${strength.color}`} 
                style={{ width: `${Math.min(100, length * (100/32))}%` }}
              />
            </div>
            <span className={`text-sm font-bold ${strength.text}`}>{strength.label}</span>
          </div>
        </div>

        <div className="space-y-6 bg-slate-50 border border-slate-200 p-6 rounded-xl">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Label className="text-base">Password Length: <span className="text-blue-600 font-bold">{length}</span></Label>
            </div>
            <input 
              type="range" 
              min="4" 
              max="128" 
              value={length} 
              onChange={(e) => setLength(Number(e.target.value))}
              className="w-full accent-blue-600 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4 pt-4">
            {[
              { id: "uppercase", label: "Uppercase (A-Z)" },
              { id: "lowercase", label: "Lowercase (a-z)" },
              { id: "numbers", label: "Numbers (0-9)" },
              { id: "symbols", label: "Symbols (!@#$)" },
            ].map(({ id, label }) => (
              <label key={id} className="flex items-center gap-3 cursor-pointer p-3 border border-slate-200 rounded-lg bg-white hover:border-blue-300 transition-colors">
                <input
                  type="checkbox"
                  className="w-5 h-5 accent-blue-600"
                  checked={options[id as keyof typeof options]}
                  onChange={(e) => {
                    const newOptions = { ...options, [id]: e.target.checked };
                    // Ensure at least one option is checked
                    if (Object.values(newOptions).some(Boolean)) {
                      setOptions(newOptions);
                    }
                  }}
                />
                <span className="text-sm font-medium text-slate-700">{label}</span>
              </label>
            ))}
          </div>
        </div>
        
        <Button size="lg" className="w-full" onClick={generatePassword}>
          <RefreshCw className="h-5 w-5 mr-2" />
          Generate New Password
        </Button>
      </div>
    </ToolPageLayout>
  );
}
